import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Check, Scale, ShieldCheck, FileSearch, Gavel } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { CALENDLY_URL, EXPERT_TOPICS, FAQS } from "../data/content";
import { Faq } from "../components/Faq";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Broker-Dealer & RIA Supervision",
    body: "Two decades supervising broker-dealer and registered investment adviser activity — sales practices, operational controls, and the supervisory frameworks regulators expect to find.",
  },
  {
    icon: FileSearch,
    title: "Regulatory Investigations & Examinations",
    body: "Firsthand experience managing SEC, FINRA, and state regulatory investigations and examinations — and translating that perspective into credible, independent analysis.",
  },
  {
    icon: Scale,
    title: "Suitability & Fiduciary Standards",
    body: "Deep command of suitability and fiduciary obligations across annuities, alternative investments, and related financial products — including senior suitability and exploitation matters.",
  },
  {
    icon: Gavel,
    title: "Testimony & Deposition Support",
    body: "Professional analysis and testimony for arbitration and legal proceedings, with support throughout the deposition process and interpretation of FINRA, SEC, and insurance regulation.",
  },
];

const ExpertWitness = () => (
  <div data-testid="expert-witness-page">
    <PageHero
      kicker="Expert Witness Services"
      lines={[<span key="a">Expertise that</span>, <span key="b">stands up in <em className="italic text-[#3E9BDC]">testimony.</em></span>]}
      intro="Expert witness consulting can make a serious difference in your case. American Elm Advisors provides expert witness consulting and testimony for arbitration cases in the investment and insurance industries — more than twenty years of compliance experience interpreting and applying the rules of FINRA, the SEC, and Departments of Insurance."
      meta={["Arbitration", "SEC", "FINRA", "State Regulators", "Departments of Insurance"]}
    />

    <section className="py-20 md:py-28" data-testid="expert-profile">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="bg-[#0A2B3D] text-white p-10 md:p-12 h-full" data-testid="jessica-profile-card">
            <img
              src="/assets/team/jessica-murray.jpg"
              alt="Jessica Murray, Partner and Chief Compliance Officer"
              className="w-28 h-28 rounded-full object-cover object-top border-2 border-[#3E9BDC]"
              data-testid="jessica-photo"
            />
            <h2 className="mt-6 font-serif font-bold text-3xl">Jessica Murray</h2>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-[#A9CFE2]">Partner & Chief Compliance Officer</p>
            <p className="mt-6 text-sm text-white/70 leading-relaxed">
              More than two decades of experience across bank-affiliated broker-dealer, investment
              adviser, insurance, annuity, and alternative investment environments. FINRA Series 6,
              7, 24, 53, 63, 65, and 99 registrations; B.S. in Management from Bryant University.
            </p>
            <p className="mt-4 text-sm text-white/70 leading-relaxed" data-testid="jessica-finra-arbitration">
              She regularly serves as an expert witness in FINRA Dispute Resolution arbitrations,
              providing independent analysis and testimony on broker-dealer and RIA supervision,
              sales practices, suitability, and fiduciary standards — with particular focus on
              alternative investments; wholesale and retail distribution; serving as investment
              manager for private funds; and annuities, from sales through compliance policies and
              procedures.
            </p>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="eyebrow text-[#3E9BDC] mb-4">Memberships</p>
              <ul className="space-y-2 text-sm text-white/85">
                <li data-testid="membership-ser">Securities Expert Roundtable</li>
                <li data-testid="membership-11c">11 Canterbury</li>
                <li data-testid="membership-nscp">National Society of Compliance Professionals</li>
              </ul>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <Kicker>Areas of Testimony</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight leading-[1.15]">
              Independent review. Industry-seasoned judgment.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="h-full p-7 bg-white border border-[#0A2B3D]/12 hover:border-[#3E9BDC]/60 transition-colors duration-500" data-testid={`testimony-pillar-${i}`}>
                  <p.icon size={22} className="text-[#3E9BDC]" />
                  <h3 className="mt-4 font-serif font-bold text-lg text-[#0A2B3D] leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="expert-topics">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker light>Case Topics</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight max-w-3xl leading-[1.15]">
            Our independent review assists with cases involving:
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
          {EXPERT_TOPICS.map((t, i) => (
            <Reveal key={t} delay={i * 0.03}>
              <div className="flex gap-3 items-baseline border-b border-white/10 pb-3" data-testid={`expert-topic-${i}`}>
                <Check size={15} className="text-[#3E9BDC] shrink-0 translate-y-0.5" />
                <span className="text-white/85 text-sm">{t}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-14">
          <div className="border border-white/15 bg-[#082838] p-8 md:p-10" data-testid="expert-process">
            <p className="eyebrow text-[#A9CFE2] mb-4">Engagement Process for Counsel</p>
            <p className="text-white/75 text-sm leading-relaxed max-w-3xl">
              We begin with a confidential conflict check and preliminary document review, followed
              by a scoped engagement letter. Our team then conducts an independent analysis of the
              documentation — applying decades of supervisory and regulatory experience — and
              supports your matter through report, deposition, and testimony as required.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <Faq items={FAQS.expert} id="expert-faq" />

    <section className="py-16 md:py-24" data-testid="expert-notable-matter">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <div className="border-l-4 border-[#3E9BDC] bg-[#EBF3F8] p-8 md:p-10">
            <p className="eyebrow text-[#3E9BDC] mb-3">Notable FINRA Arbitration Matter</p>
            <h3 className="font-serif font-bold text-xl md:text-2xl text-[#0A2B3D] leading-snug">
              A first-of-its-kind award in investor cherry-picking fraud
            </h3>
            <p className="mt-4 text-sm text-[#475569] leading-relaxed">
              In <em>Cook &amp; McCleary v. TD Ameritrade / Charles Schwab &amp; Co.</em> (FINRA
              Dispute Resolution Case No. 23-03586, 2025), a FINRA arbitration panel found a major
              brokerage liable to investors — including punitive damages — for facilitating and
              concealing a registered investment adviser's multi-year cherry-picking scheme. It is
              widely reported as the first ruling holding a major custodian accountable to investors
              for fraud carried out by an RIA on its platform.
            </p>
            <a
              href="https://www.newswire.com/news/finra-arbitrators-find-major-brokerage-liable-to-investors-for-22569714"
              target="_blank"
              rel="noreferrer"
              data-testid="expert-notable-link"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors underline underline-offset-4"
            >
              Read the case coverage <ArrowRight size={15} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>

    <div className="max-w-4xl mx-auto px-5 sm:px-8 -mt-6 md:-mt-10 pb-4" data-testid="expert-faq-explore-more">
      <Link to="/expert-witness-faq" data-testid="expert-explore-more-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors underline underline-offset-4">
        Explore more expert witness questions <ArrowRight size={15} />
      </Link>
    </div>

    <section className="bg-[#A9CFE2] py-16 md:py-24" data-testid="expert-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div>
          <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight">Considering an independent expert?</h2>
          <p className="mt-3 text-[#0A2B3D]/75 max-w-xl">Start a confidential pre-retention intake — public information only, with a conflict check first. Ideal for FINRA arbitration and other securities and insurance matters.</p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
          <Link to="/expert-witness-intake" data-testid="expert-cta-button" className="btn-primary">
            Start Pre-Retention Intake <ArrowRight size={16} />
          </Link>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="expert-calendly-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2B3D] hover:text-[#082838] underline underline-offset-4">
            <CalendarClock size={16} /> Or schedule a 15-minute call
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default ExpertWitness;
